import React, { useState, useEffect, useCallback } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { AlertModal } from './AlertModal';
import { SvIcon } from './servers/svIcons';
import { formatLocalDateTime, formatLocalTime } from '../utils/timeUtils';
import { refreshPublicConfig } from '../utils/publicConfig';
import UpdatesPanel from './settings/UpdatesPanel';
import ApiTrafficPanel from './settings/ApiTrafficPanel';
import './settings/settings.css';

// Chat prefix color: per-admin setting. Source of truth for the bytes:
// https://github.com/PremyslTalich/ColorVariables/blob/master/addons/sourcemod/scripting/includes/colorvariables.inc
// Mirrors server/routes/settings.js ALLOWED_CHAT_PREFIX_COLORS — keep both in sync.
const PREFIX_COLOR_OPTIONS = [
    { key: 'darkred', name: 'Dark Red', hex: '#b91c1c' },
    { key: 'purple', name: 'Purple', hex: '#a855f7' },
    { key: 'green', name: 'Green', hex: '#84cc16' },
    { key: 'gold', name: 'Gold', hex: '#f59e0b' },
];

const DISCORD_PATH = 'M20.317 4.37a19.791 19.791 0 00-4.885-1.515.074.074 0 00-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 00-5.487 0 12.64 12.64 0 00-.617-1.25.077.077 0 00-.079-.037A19.736 19.736 0 003.677 4.37a.07.07 0 00-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 00.031.057 19.9 19.9 0 005.993 3.03.078.078 0 00.084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 00-.041-.106 13.107 13.107 0 01-1.872-.892.077.077 0 01-.008-.128 10.2 10.2 0 00.372-.292.074.074 0 01.077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 01.078.01c.12.098.246.198.373.292a.077.077 0 01-.006.127 12.299 12.299 0 01-1.873.892.077.077 0 00-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 00.084.028 19.839 19.839 0 006.002-3.03.077.077 0 00.032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 00-.031-.03z';
const DiscordIcon = ({ size = 16 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d={DISCORD_PATH} /></svg>;

const isMaskedSecret = (value) => {
    const text = String(value || '');
    return text.startsWith('****') || text.startsWith('••••');
};
const cfgToOnline = (cfgText) => {
    const lines = cfgText.split('\n').map(l => l.trim()).filter(l => l && !l.startsWith('//'));
    return lines.length > 0 ? lines.join(';') : '';
};

/** One settings block. `id` is its link anchor (/admin/settings/<tab>#<id>). */
function Section({ id, tab, icon, tone, title, badge, desc, wide, children }) {
    const { t } = useTranslation();
    const [copied, setCopied] = useState(false);
    const copyLink = () => {
        const url = `${window.location.origin}/admin/settings/${tab}#${id}`;
        window.history.replaceState(null, '', `#${id}`);
        if (navigator.clipboard) navigator.clipboard.writeText(url).catch(() => { });
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };
    return (
        <section id={id} className={`st-section${wide ? ' wide' : ''}`}>
            <div className="st-head">
                <span className="st-ic" style={{ '--st-tone': tone }}>{icon}</span>
                <div className="st-head-text">
                    <h3>{title}{badge}</h3>
                    {desc && <p>{desc}</p>}
                </div>
                <button type="button" className={`st-anchor${copied ? ' done' : ''}`} onClick={copyLink} title={t('settings.copyLink', 'Copy link to this setting')}>
                    <SvIcon name={copied ? 'check' : 'link'} size={13} />
                </button>
            </div>
            {children}
        </section>
    );
}

const Badge = ({ on, onText, offText, color = '#22c55e' }) => (
    <span className="st-badge" style={{ '--st-b': on ? color : '#9ca3af' }}>{on ? onText : offText}</span>
);

function SwitchRow({ on, onClick, title, desc, disabled, color }) {
    return (
        <button type="button" className="st-switch-row" onClick={onClick} disabled={disabled} role="switch" aria-checked={!!on}>
            <span className="txt"><b>{title}</b>{desc && <small>{desc}</small>}</span>
            <span className={`st-switch${on ? ' on' : ''}`} style={color ? { '--st-sw': color } : undefined} />
        </button>
    );
}

const Busy = ({ busy, children, label }) => (busy ? <><span className="st-spin" />{label}</> : children);

function Settings() {
    const { t } = useTranslation();
    const { user, replaceToken, logoutEverywhere } = useAuth();
    const [confirmSignOutAll, setConfirmSignOutAll] = useState(false);
    const { tab: tabParam } = useParams();
    const location = useLocation();
    const isSuperAdmin = user?.role === 'super_admin' || user?.role === 'admin';

    const TABS = [
        { id: 'account', label: t('settings.tabAccount', 'Account'), icon: 'user' },
        { id: 'match', label: t('settings.tabMatch', 'Match config'), icon: 'gear', superAdminOnly: true },
        { id: 'integrations', label: t('settings.tabIntegrations', 'Integrations'), icon: 'link', superAdminOnly: true },
        { id: 'system', label: t('settings.tabSystem', 'System'), icon: 'server', superAdminOnly: true },
        { id: 'traffic', label: t('settings.tabTraffic', 'API traffic'), icon: 'zap', superAdminOnly: true },
        { id: 'updates', label: t('settings.tabUpdates', 'Updates'), icon: 'download', superAdminOnly: true },
    ];
    const visibleTabs = TABS.filter(x => !x.superAdminOnly || isSuperAdmin);
    const tab = visibleTabs.some(x => x.id === tabParam) ? tabParam : null;

    const [alert, setAlert] = useState({ show: false, message: '', type: 'info' });
    const notify = (message, type = 'success') => setAlert({ show: true, message, type });
    const fail = (error, fallback) => notify(error?.response?.data?.error || fallback, 'error');

    // ── account ──
    const [pwdForm, setPwdForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
    const [pwdSaving, setPwdSaving] = useState(false);
    const [webhookUrl, setWebhookUrl] = useState('');
    const [webhookActive, setWebhookActive] = useState(false);
    const [webhookEvents, setWebhookEvents] = useState([]);
    const [validEvents, setValidEvents] = useState([]);
    const [webhookSaving, setWebhookSaving] = useState(false);
    const [webhookTesting, setWebhookTesting] = useState(false);

    // ── match config ──
    const [chatPrefix, setChatPrefix] = useState('[Flux]');
    const [chatPrefixSaving, setChatPrefixSaving] = useState(false);
    const [chatPrefixColor, setChatPrefixColor] = useState('green');
    const [chatPrefixColorSaving, setChatPrefixColorSaving] = useState(false);
    const [panelPublicUrl, setPanelPublicUrl] = useState('');
    const [panelUrlSaving, setPanelUrlSaving] = useState(false);
    const [cfgWarmup, setCfgWarmup] = useState('');
    const [cfgKnife, setCfgKnife] = useState('');
    const [cfgLive, setCfgLive] = useState('');
    const [cfgSaving, setCfgSaving] = useState(false);
    const [cfgLoaded, setCfgLoaded] = useState(false);
    const [hideAboutPage, setHideAboutPage] = useState(false);
    const [hideAboutSaving, setHideAboutSaving] = useState(false);
    const [hideBetaBanner, setHideBetaBanner] = useState(false);
    const [hideBetaSaving, setHideBetaSaving] = useState(false);
    const [allowIndexing, setAllowIndexing] = useState(true); // app_settings.allow_search_indexing (default on)
    const [allowIndexingSaving, setAllowIndexingSaving] = useState(false);

    // ── integrations ──
    const [demoMode, setDemoMode] = useState('off'); // 'off' | 'auto'
    const [demoSaving, setDemoSaving] = useState(false);
    const [uploadMethod, setUploadMethod] = useState('gofile');
    const [rcloneRemoteName, setRcloneRemoteName] = useState('gdrive');
    const [rcloneRemoteFolder, setRcloneRemoteFolder] = useState('flux-demos');
    const [rcloneInstalled, setRcloneInstalled] = useState(false);
    const [rcloneRemoteConfigured, setRcloneRemoteConfigured] = useState(false);
    const [uploadSaving, setUploadSaving] = useState(false);
    const [rcloneTesting, setRcloneTesting] = useState(false);
    const [rcloneTestingUpload, setRcloneTestingUpload] = useState(false);
    const [pixeldrainApiKey, setPixeldrainApiKey] = useState('');
    const [pixeldrainKeySet, setPixeldrainKeySet] = useState(false);
    const [storagetoApiKey, setStoragetoApiKey] = useState('');
    const [storagetoKeySet, setStoragetoKeySet] = useState(false);
    const [storagetoTesting, setStoragetoTesting] = useState(false);
    const [faceitApiKey, setFaceitApiKey] = useState('');
    const [faceitKeySet, setFaceitKeySet] = useState(false);
    const [faceitSaving, setFaceitSaving] = useState(false);
    const [faceitTesting, setFaceitTesting] = useState(false);
    const [steamApiKey, setSteamApiKey] = useState('');
    const [steamKeySet, setSteamKeySet] = useState(false);
    const [steamSaving, setSteamSaving] = useState(false);

    // ── system ──
    const [onlineUsers, setOnlineUsers] = useState([]);
    const [onlineUsersLoading, setOnlineUsersLoading] = useState(false);
    const [healthEvents, setHealthEvents] = useState([]);
    const [healthLoading, setHealthLoading] = useState(false);
    const [healthDownloading, setHealthDownloading] = useState(false);

    // ── initial loads ──
    useEffect(() => {
        axios.get('/api/settings/webhook').then(res => {
            setWebhookUrl(res.data.webhook_url || '');
            setWebhookActive(!!res.data.is_active);
            setWebhookEvents(res.data.enabled_events || []);
            setValidEvents(res.data.valid_events || []);
        }).catch(err => console.error('Failed to load webhook config', err));
    }, []);

    useEffect(() => {
        if (!isSuperAdmin) return;
        axios.get('/api/settings').then(res => {
            setDemoMode(res.data.auto_demo_download === 'true' ? 'auto' : 'off');
            setHideAboutPage(res.data.hide_about_page === 'true');
            setHideBetaBanner(res.data.hide_beta_banner === 'true');
            setAllowIndexing(res.data.allow_search_indexing !== 'false');
            setSteamApiKey(res.data.steam_api_key || '');
            setSteamKeySet(!!res.data.steam_api_key_set);
            setPanelPublicUrl(res.data.panel_public_url || '');
        }).catch(err => console.error('Failed to load settings', err));
        axios.get('/api/settings/chat-prefix').then(res => {
            if (res.data.chat_prefix) setChatPrefix(res.data.chat_prefix);
        }).catch(err => console.error('Failed to load chat prefix', err));
        axios.get('/api/settings/chat-prefix-color').then(res => {
            // API returns a raw byte; reverse-look up the key from the byte.
            const match = Object.entries(res.data.allowed || {}).find(([, v]) => v.byte === res.data.chat_prefix_color);
            if (match) setChatPrefixColor(match[0]);
        }).catch(err => console.error('Failed to load chat prefix color', err));
        axios.get('/api/settings/rclone/status').then(res => {
            setUploadMethod(res.data.upload_method || 'gofile');
            setRcloneRemoteName(res.data.remote_name || 'gdrive');
            setRcloneRemoteFolder(res.data.remote_folder || 'flux-demos');
            setRcloneInstalled(res.data.installed || false);
            setRcloneRemoteConfigured(res.data.remote_configured || false);
            if (res.data.pixeldrain_api_key) setPixeldrainApiKey(res.data.pixeldrain_api_key);
            setPixeldrainKeySet(!!res.data.pixeldrain_api_key_set);
            if (res.data.storageto_api_key) setStoragetoApiKey(res.data.storageto_api_key);
            setStoragetoKeySet(!!res.data.storageto_api_key_set);
        }).catch(err => console.error('Failed to load rclone status', err));
        axios.get('/api/settings/faceit/status').then(res => {
            setFaceitApiKey(res.data.faceit_api_key || '');
            setFaceitKeySet(!!res.data.faceit_api_key_set);
        }).catch(err => console.error('Failed to load FACEIT status', err));
    }, [isSuperAdmin]);

    // Server configs: loaded the first time the Match config tab is opened
    useEffect(() => {
        if (tab !== 'match' || cfgLoaded) return;
        axios.get('/api/settings/server-configs').then(res => {
            setCfgWarmup(res.data.warmup || '');
            setCfgKnife(res.data.knife || '');
            setCfgLive(res.data.live || '');
            setCfgLoaded(true);
        }).catch(err => console.error('Failed to load server configs', err));
    }, [tab, cfgLoaded]);

    const loadOnlineUsers = useCallback(async (quiet) => {
        if (!quiet) setOnlineUsersLoading(true);
        try {
            const res = await axios.get('/api/settings/online-users');
            setOnlineUsers(Array.isArray(res.data.users) ? res.data.users : []);
        } catch (error) {
            if (!quiet) fail(error, t('settings.onlineUsersLoadFailed', 'Failed to load online users'));
        } finally {
            if (!quiet) setOnlineUsersLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [t]);

    const loadHealthEvents = useCallback(async () => {
        setHealthLoading(true);
        try {
            const res = await axios.get('/api/settings/system-health-events?days=30&limit=500');
            setHealthEvents(Array.isArray(res.data.events) ? res.data.events : []);
        } catch (error) {
            fail(error, t('settings.healthLogsLoadFailed', 'Failed to load system health logs'));
        } finally {
            setHealthLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [t]);

    // System tab: load once on open; online users refresh every 5 s while the tab is open and visible
    useEffect(() => {
        if (tab !== 'system') return;
        loadOnlineUsers(false);
        loadHealthEvents();
        const timer = setInterval(() => { if (!document.hidden) loadOnlineUsers(true); }, 5000);
        return () => clearInterval(timer);
    }, [tab, loadOnlineUsers, loadHealthEvents]);

    // #anchor → scroll to that section and flash it
    useEffect(() => {
        const id = (location.hash || '').slice(1);
        if (!id) { window.scrollTo({ top: 0 }); return; }
        const timer = setTimeout(() => {
            const el = document.getElementById(id);
            if (!el) return;
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            el.classList.remove('flash');
            void el.offsetWidth; // restart the animation
            el.classList.add('flash');
        }, 60);
        return () => clearTimeout(timer);
    }, [location.hash, tab]);

    // ── handlers ──
    const handlePwdSubmit = async (e) => {
        e.preventDefault();
        if (pwdForm.newPassword !== pwdForm.confirmPassword) return notify(t('settings.passwordsMismatch', 'New passwords do not match'), 'error');
        setPwdSaving(true);
        try {
            const r = await axios.post('/api/auth/change-password', { currentPassword: pwdForm.currentPassword, newPassword: pwdForm.newPassword });
            replaceToken(r.data && r.data.token); // other browsers are signed out; this one keeps going
            notify(t('settings.passwordChangedOthers', 'Password changed - you were signed out on every other browser'));
            setPwdForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        } catch (error) {
            fail(error, t('settings.passwordChangeFailed', 'Failed to change password'));
        } finally { setPwdSaving(false); }
    };

    const toggleWebhookEvent = (evt) => setWebhookEvents(prev => prev.includes(evt) ? prev.filter(e => e !== evt) : [...prev, evt]);
    const saveWebhookConfig = async () => {
        setWebhookSaving(true);
        try {
            await axios.post('/api/settings/webhook', { webhook_url: webhookUrl, enabled_events: webhookEvents, is_active: webhookActive });
            notify(t('settings.webhookSaved', 'Webhook config saved!'));
        } catch (error) {
            fail(error, t('settings.webhookSaveFailed', 'Failed to save webhook config'));
        } finally { setWebhookSaving(false); }
    };
    const testWebhook = async () => {
        if (!webhookUrl.trim()) return notify(t('settings.enterWebhookFirst', 'Enter a webhook URL first'), 'error');
        setWebhookTesting(true);
        try {
            await axios.post('/api/settings/webhook/test', { webhook_url: webhookUrl });
            notify(t('settings.testNotifSent', 'Test notification sent! Check your Discord channel.'));
        } catch (error) {
            fail(error, t('settings.testNotifFailed', 'Failed to send test notification'));
        } finally { setWebhookTesting(false); }
    };

    const saveChatPrefix = async () => {
        if (!chatPrefix.trim()) return notify(t('settings.prefixEmpty', 'Prefix cannot be empty'), 'error');
        setChatPrefixSaving(true);
        try {
            await axios.post('/api/settings/chat-prefix', { chat_prefix: chatPrefix.trim() });
            notify(t('settings.prefixUpdated', 'Chat prefix updated to "{{prefix}}"', { prefix: chatPrefix.trim() }));
        } catch (error) {
            fail(error, t('settings.prefixUpdateFailed', 'Failed to update prefix'));
        } finally { setChatPrefixSaving(false); }
    };
    const saveChatPrefixColor = async () => {
        setChatPrefixColorSaving(true);
        try {
            await axios.post('/api/settings/chat-prefix-color', { key: chatPrefixColor });
            const opt = PREFIX_COLOR_OPTIONS.find(o => o.key === chatPrefixColor);
            notify(t('settings.prefixColorUpdated', 'Chat prefix color updated to {{color}}', { color: opt ? opt.name : chatPrefixColor }));
        } catch (error) {
            fail(error, t('settings.prefixColorUpdateFailed', 'Failed to update prefix color'));
        } finally { setChatPrefixColorSaving(false); }
    };
    const savePanelPublicUrl = async () => {
        setPanelUrlSaving(true);
        try {
            await axios.post('/api/settings', { key: 'panel_public_url', value: panelPublicUrl.trim() });
            notify(t('settings.panelUrlSaved', 'Panel URL saved'));
        } catch (error) {
            fail(error, t('settings.panelUrlSaveFailed', 'Failed to save panel URL'));
        } finally { setPanelUrlSaving(false); }
    };
    const saveServerConfigs = async () => {
        setCfgSaving(true);
        try {
            await axios.post('/api/settings/server-configs', {
                warmup: cfgWarmup, knife: cfgKnife, live: cfgLive,
                online_warmup: cfgToOnline(cfgWarmup), online_knife: cfgToOnline(cfgKnife), online_live: cfgToOnline(cfgLive),
            });
            notify(t('settings.configsSaved', 'Server configs saved successfully!'));
        } catch (error) {
            fail(error, t('settings.configsSaveFailed', 'Failed to save server configs'));
        } finally { setCfgSaving(false); }
    };
    const toggleSetting = async (key, next, setValue, setSaving, onMsg, offMsg) => {
        setSaving(true);
        try {
            await axios.post('/api/settings', { key, value: next.toString() });
            setValue(next);
            refreshPublicConfig();
            notify(next ? onMsg : offMsg);
        } catch (error) {
            fail(error, t('settings.updateFailed', 'Failed to update setting'));
        } finally { setSaving(false); }
    };

    const toggleDemoMode = async () => {
        const nextMode = demoMode === 'off' ? 'auto' : 'off';
        setDemoSaving(true);
        try {
            await axios.post('/api/settings', { key: 'allow_demo_requests', value: (nextMode !== 'off').toString() });
            await axios.post('/api/settings', { key: 'auto_demo_download', value: (nextMode === 'auto').toString() });
            setDemoMode(nextMode);
            notify(nextMode === 'auto' ? t('settings.demoEnabled', 'Demo auto-download enabled.') : t('settings.demoDisabled', 'Demo auto-download disabled.'));
        } catch (error) {
            fail(error, t('settings.updateFailed', 'Failed to update setting'));
        } finally { setDemoSaving(false); }
    };
    const saveUploadConfig = async () => {
        setUploadSaving(true);
        try {
            await axios.post('/api/settings', { key: 'demo_upload_method', value: uploadMethod });
            await axios.post('/api/settings', { key: 'rclone_remote_name', value: rcloneRemoteName });
            await axios.post('/api/settings', { key: 'rclone_remote_folder', value: rcloneRemoteFolder });
            // Only save an API key the user actually typed (not the masked placeholder)
            if (pixeldrainApiKey && !isMaskedSecret(pixeldrainApiKey)) {
                await axios.post('/api/settings', { key: 'pixeldrain_api_key', value: pixeldrainApiKey });
                setPixeldrainKeySet(true);
            }
            if (storagetoApiKey && !isMaskedSecret(storagetoApiKey)) {
                await axios.post('/api/settings', { key: 'storageto_api_key', value: storagetoApiKey });
                setStoragetoKeySet(true);
            }
            notify(t('settings.uploadSaved', 'Upload config saved!'));
        } catch (error) {
            fail(error, t('settings.uploadSaveFailed', 'Failed to save config'));
        } finally { setUploadSaving(false); }
    };
    const testRcloneConnection = async () => {
        setRcloneTesting(true);
        try {
            await axios.post('/api/settings/rclone/test');
            notify(t('settings.rcloneConnOk', 'Rclone connection successful!'));
        } catch (error) {
            fail(error, t('settings.connTestFailed', 'Connection test failed'));
        } finally { setRcloneTesting(false); }
    };
    const testRcloneUpload = async () => {
        setRcloneTestingUpload(true);
        try {
            const res = await axios.post('/api/settings/rclone/test-upload');
            notify(res.data.message || t('settings.uploadTestPassed', 'Upload test passed!'));
        } catch (error) {
            fail(error, t('settings.uploadTestFailed', 'Upload test failed'));
        } finally { setRcloneTestingUpload(false); }
    };
    const testStoragetoToken = async () => {
        setStoragetoTesting(true);
        try {
            const res = await axios.post('/api/settings/storageto/test');
            notify(res.data.premium
                ? t('settings.storagetoTestPaid', 'storage.to token valid (paid plan - demos are kept permanently).')
                : t('settings.storagetoTestFree', 'storage.to token valid (free plan - demos expire after 7 days).'));
        } catch (error) {
            fail(error, t('settings.connTestFailed', 'Connection test failed'));
        } finally { setStoragetoTesting(false); }
    };
    const saveFaceitConfig = async () => {
        setFaceitSaving(true);
        try {
            const value = (faceitApiKey || '').trim();
            if (!isMaskedSecret(value)) {
                const res = await axios.post('/api/settings', { key: 'faceit_api_key', value });
                setFaceitApiKey(res.data.value || '');
                setFaceitKeySet(!!value);
            }
            notify(t('settings.faceitSaved', 'FACEIT config saved!'));
        } catch (error) {
            fail(error, t('settings.faceitSaveFailed', 'Failed to save FACEIT config'));
        } finally { setFaceitSaving(false); }
    };
    const testFaceitConfig = async () => {
        setFaceitTesting(true);
        try {
            const value = (faceitApiKey || '').trim();
            await axios.post('/api/settings/faceit/test', value && !isMaskedSecret(value) ? { api_key: value } : {});
            notify(t('settings.faceitKeyValid', 'FACEIT API key is valid'));
        } catch (error) {
            fail(error, t('settings.faceitKeyTestFailed', 'FACEIT API key test failed'));
        } finally { setFaceitTesting(false); }
    };
    const saveSteamApiKey = async () => {
        setSteamSaving(true);
        try {
            const value = (steamApiKey || '').trim();
            if (!isMaskedSecret(value)) {
                const res = await axios.post('/api/settings', { key: 'steam_api_key', value });
                setSteamApiKey(res.data.value || '');
                setSteamKeySet(!!value);
            }
            notify(t('settings.steamSaved', 'Steam API key saved.'));
        } catch (error) {
            fail(error, t('settings.steamSaveFailed', 'Failed to save Steam API key'));
        } finally { setSteamSaving(false); }
    };

    const downloadHealthEvents = async () => {
        setHealthDownloading(true);
        try {
            const res = await axios.get('/api/settings/system-health-events/download?days=30', { responseType: 'blob' });
            const match = (res.headers['content-disposition'] || '').match(/filename="?([^"]+)"?/i);
            const filename = match ? match[1] : `system-health-events-${new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19)}.csv`;
            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
            const link = document.createElement('a');
            link.href = url; link.download = filename;
            document.body.appendChild(link); link.click(); link.remove();
            window.URL.revokeObjectURL(url);
            notify(t('settings.healthLogDownloaded', 'System health log downloaded.'));
        } catch (error) {
            fail(error, t('settings.healthLogDownloadFailed', 'Failed to download system health log'));
        } finally { setHealthDownloading(false); }
    };

    if (!tab) return <Navigate to={`/admin/settings/account${location.hash || ''}`} replace />;

    const eventLabels = {
        tech_pause: t('settings.evtTechPause', 'Tech Pause'), match_created: t('settings.evtMatchCreated', 'Match Created'), match_live: t('settings.evtMatchLive', 'Match Live'),
        match_finished: t('settings.evtMatchFinished', 'Match Finished'), match_cancelled: t('settings.evtMatchCancelled', 'Match Cancelled'), match_forfeited: t('settings.evtMatchForfeited', 'Match Forfeited'),
        round_restored: t('settings.evtRoundRestored', 'Round Restored'), server_offline: t('settings.evtServerOffline', 'Server Offline'), server_recovered: t('settings.evtServerRecovered', 'Server Recovered'),
        map_changed: t('settings.evtMapChanged', 'Map Changed'), force_pause: t('settings.evtForcePause', 'Force Pause'), knife_won: t('settings.evtKnifeWon', 'Knife Won'),
    };
    const UPLOAD_OPTIONS = [
        { key: 'gofile', label: 'Gofile', color: '#4285f4', desc: t('settings.gofileDesc', 'Free, no setup needed') },
        { key: 'pixeldrain', label: 'Pixeldrain', color: '#a855f7', desc: t('settings.pixeldrainDesc', 'Requires API key') },
        { key: 'storageto', label: 'storage.to', color: '#f97316', desc: t('settings.storagetoDesc', 'Requires API token') },
        { key: 'rclone', label: 'Google Drive', color: '#34a853', desc: t('settings.rcloneDesc', 'Your own storage via rclone') },
    ];
    const prefixColor = (PREFIX_COLOR_OPTIONS.find(o => o.key === chatPrefixColor) || PREFIX_COLOR_OPTIONS[2]).hex;

    // Sections per tab: [id, label] — drives the jump links
    const JUMP = {
        account: [['password', t('settings.changePassword', 'Change Password')], ['webhook', t('settings.discordWebhooks', 'Discord Webhooks')]],
        match: [['chat-prefix', t('settings.chatPrefix', 'Chat Prefix')], ['panel-url', t('settings.panelUrl', 'Panel public URL')], ['public-site', t('settings.publicSite', 'Public site')], ['server-configs', t('settings.serverConfig', 'Server Config')]],
        integrations: [['demos', t('settings.demos', 'Demos')], ['faceit', 'FACEIT'], ['steam', t('settings.steamApiKey', 'Steam API Key')]],
        system: [['online-users', t('settings.onlineUsers', 'Online Users')], ['health', t('settings.systemHealth', 'System Health Logs')]],
        traffic: [['api-traffic', t('settings.apiTraffic', 'API traffic')]],
        updates: [['panel-update', t('settings.panelUpdates', 'Panel updates')]],
    };

    return (
        <div className="st-page">
            <div>
                <h1 className="st-title">{t('nav.settings', 'Settings')}</h1>
                <p className="st-subtitle">{t('settings.pageSubtitle', 'Manage your account, integrations, and panel configuration.')}</p>
            </div>

            <nav className="st-tabs" aria-label={t('nav.settings', 'Settings')}>
                {visibleTabs.map(x => (
                    <Link key={x.id} to={`/admin/settings/${x.id}`} className={`st-tab${tab === x.id ? ' on' : ''}`} aria-current={tab === x.id ? 'page' : undefined}>
                        <SvIcon name={x.icon} size={15} />{x.label}
                    </Link>
                ))}
            </nav>

            <div className="st-jump">
                {JUMP[tab].map(([id, label]) => <Link key={id} to={`/admin/settings/${tab}#${id}`}>{label}</Link>)}
            </div>

            {/* ── Account ── */}
            {tab === 'account' && (
                <div className="st-grid">
                    <Section tab={tab} id="password" icon={<SvIcon name="lock" />} tone="#22c55e" title={t('settings.changePassword', 'Change Password')} desc={t('settings.changePasswordDesc', 'Update your account password.')}>
                        <form onSubmit={handlePwdSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                            <div className="st-field">
                                <label>{t('settings.currentPassword', 'Current Password')}</label>
                                <input className="st-input" type="password" autoComplete="current-password" required value={pwdForm.currentPassword} onChange={e => setPwdForm({ ...pwdForm, currentPassword: e.target.value })} />
                            </div>
                            <div className="st-field">
                                <label>{t('settings.newPassword', 'New Password')}</label>
                                <input className="st-input" type="password" autoComplete="new-password" required minLength={6} value={pwdForm.newPassword} onChange={e => setPwdForm({ ...pwdForm, newPassword: e.target.value })} />
                            </div>
                            <div className="st-field">
                                <label>{t('settings.confirmNewPassword', 'Confirm New Password')}</label>
                                <input className="st-input" type="password" autoComplete="new-password" required minLength={6} value={pwdForm.confirmPassword} onChange={e => setPwdForm({ ...pwdForm, confirmPassword: e.target.value })} />
                            </div>
                            <div className="st-actions">
                                <button type="submit" className="st-btn primary" disabled={pwdSaving}>
                                    <Busy busy={pwdSaving} label={t('settings.saving', 'Saving...')}>{t('settings.updatePassword', 'Update Password')}</Busy>
                                </button>
                            </div>
                        </form>
                        <div className="st-field st-subhead">
                            <label>{t('settings.signOutAll', 'Sign out everywhere')}</label>
                            <p>
                                {t('settings.signOutAllHint', 'Ends your login on every browser and device, this one too. Use it if you signed in on a shared PC or think someone else has your login. API keys are not affected.')}
                            </p>
                            {confirmSignOutAll ? (
                                <div className="st-actions">
                                    <button type="button" className="st-btn danger" onClick={() => logoutEverywhere().catch(e => fail(e, t('settings.signOutAllFailed', 'Could not sign out everywhere')))}>{t('settings.signOutAllConfirm', 'Yes, sign out everywhere')}</button>
                                    <button type="button" className="st-btn" onClick={() => setConfirmSignOutAll(false)}>{t('common.cancel', 'Cancel')}</button>
                                </div>
                            ) : (
                                <div className="st-actions">
                                    <button type="button" className="st-btn" onClick={() => setConfirmSignOutAll(true)}>{t('settings.signOutAll', 'Sign out everywhere')}</button>
                                </div>
                            )}
                        </div>
                    </Section>

                    <Section tab={tab} id="webhook" icon={<DiscordIcon />} tone="#5865F2" title={t('settings.discordWebhooks', 'Discord Webhooks')}
                        badge={<Badge on={webhookActive && !!webhookUrl} onText={t('settings.active', 'Active')} offText={t('settings.badgeOff', 'Off')} />}
                        desc={t('settings.discordWebhooksDesc', 'Match events, tech pauses, server status.')}>
                        <div className="st-field">
                            <label>{t('settings.webhookUrl', 'Webhook URL')}</label>
                            <input className="st-input mono" type="url" value={webhookUrl} onChange={e => setWebhookUrl(e.target.value)} placeholder="https://discord.com/api/webhooks/..." />
                            <span className="st-hint">{t('settings.webhookUrlHint', 'Create a webhook in your Discord channel settings.')}</span>
                        </div>
                        <SwitchRow on={webhookActive} color="#5865F2" onClick={() => setWebhookActive(v => !v)}
                            title={webhookActive ? t('settings.notificationsEnabled', 'Notifications enabled') : t('settings.notificationsDisabled', 'Notifications disabled')} />
                        <div className="st-field">
                            <div className="st-row" style={{ justifyContent: 'space-between' }}>
                                <label className="st-label">{t('settings.events', 'Events')} ({webhookEvents.length}/{validEvents.length})</label>
                                <span className="st-row">
                                    <button type="button" className="st-btn small" onClick={() => setWebhookEvents([...validEvents])}>{t('common.all', 'All')}</button>
                                    <button type="button" className="st-btn small" onClick={() => setWebhookEvents([])}>{t('common.none', 'None')}</button>
                                </span>
                            </div>
                            <div className="st-events two">
                                {validEvents.map(evt => {
                                    const on = webhookEvents.includes(evt);
                                    return (
                                        <button key={evt} type="button" className={`st-event${on ? ' on' : ''}`} aria-pressed={on} onClick={() => toggleWebhookEvent(evt)}>
                                            <span className="box">{on && <SvIcon name="check" size={11} />}</span>{eventLabels[evt] || evt}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                        <div className="st-actions">
                            <span className="left">
                                <button type="button" className="st-btn" onClick={testWebhook} disabled={webhookTesting || !webhookUrl.trim()}>
                                    <Busy busy={webhookTesting} label={t('settings.sending', 'Sending...')}><SvIcon name="send" size={13} />{t('settings.testWebhook', 'Test Webhook')}</Busy>
                                </button>
                            </span>
                            <button type="button" className="st-btn primary" onClick={saveWebhookConfig} disabled={webhookSaving}>
                                <Busy busy={webhookSaving} label={t('settings.saving', 'Saving...')}>{t('common.save', 'Save')}</Busy>
                            </button>
                        </div>
                    </Section>
                </div>
            )}

            {/* ── Match config ── */}
            {tab === 'match' && (
                <div className="st-grid">
                    <Section tab={tab} id="chat-prefix" icon={<SvIcon name="chat" />} tone="#f59e0b" title={t('settings.chatPrefix', 'Chat Prefix')} desc={t('settings.chatPrefixDesc', 'Your in-game chat prefix for matches.')}>
                        <div className="st-field">
                            <label>{t('settings.prefixText', 'Text')}</label>
                            <div className="st-row">
                                <input className="st-input mono" type="text" value={chatPrefix} onChange={e => setChatPrefix(e.target.value)} placeholder="[Flux]" maxLength={30} />
                                <button type="button" className="st-btn primary" onClick={saveChatPrefix} disabled={chatPrefixSaving}>
                                    <Busy busy={chatPrefixSaving} label="…">{t('common.save', 'Save')}</Busy>
                                </button>
                            </div>
                        </div>
                        <div className="st-field">
                            <label>{t('settings.chatPrefixColor', 'Chat Prefix Color')}</label>
                            <div className="st-row">
                                <div className="st-swatches" style={{ flex: 1 }}>
                                    {PREFIX_COLOR_OPTIONS.map(opt => (
                                        <button key={opt.key} type="button" className={`st-swatch${chatPrefixColor === opt.key ? ' on' : ''}`} style={{ '--st-c': opt.hex }} onClick={() => setChatPrefixColor(opt.key)}>
                                            <i />{opt.name}
                                        </button>
                                    ))}
                                </div>
                                <button type="button" className="st-btn primary" onClick={saveChatPrefixColor} disabled={chatPrefixColorSaving}>
                                    <Busy busy={chatPrefixColorSaving} label="…">{t('common.save', 'Save')}</Busy>
                                </button>
                            </div>
                            <span className="st-hint">{t('settings.chatPrefixColorDesc', 'In-game color of the prefix in CS2 chat. Applies to matches you create.')}</span>
                        </div>
                        <div className="st-preview">
                            {t('settings.preview', 'Preview')}: <span style={{ color: prefixColor, fontWeight: 600 }}>{chatPrefix || '[Flux]'}</span> <span style={{ color: 'var(--text-primary)' }}>{t('settings.roundIsLive', 'Round is live!')}</span>
                        </div>
                    </Section>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem', minWidth: 0 }}>
                        <Section tab={tab} id="panel-url" icon={<SvIcon name="globe" />} tone="var(--accent-blue)" title={t('settings.panelUrl', 'Panel public URL')} desc={t('settings.panelUrlDesc', "Your panel's public URL for Discord webhooks and veto links.")}>
                            <div className="st-row">
                                <input className="st-input mono" type="url" value={panelPublicUrl} onChange={e => setPanelPublicUrl(e.target.value)} placeholder="https://panel.yourdomain.com" />
                                <button type="button" className="st-btn primary" onClick={savePanelPublicUrl} disabled={panelUrlSaving}>
                                    <Busy busy={panelUrlSaving} label="…">{t('common.save', 'Save')}</Busy>
                                </button>
                            </div>
                            <span className="st-hint">{t('settings.panelUrlHint', 'Used for veto links in Discord webhooks. Leave empty to use the environment variables.')}</span>
                        </Section>

                        <Section tab={tab} id="public-site" icon={<SvIcon name="layout" />} tone="#06b6d4" title={t('settings.publicSite', 'Public site')} desc={t('settings.publicSiteDesc', 'What visitors see without logging in.')}>
                            <SwitchRow on={!hideAboutPage} disabled={hideAboutSaving} color="#22c55e"
                                onClick={() => toggleSetting('hide_about_page', !hideAboutPage, setHideAboutPage, setHideAboutSaving, t('settings.aboutHidden', 'About page hidden from the public site.'), t('settings.aboutShown', 'About page is now visible on the public site.'))}
                                title={`${t('settings.aboutPage', 'About Page')}: ${hideAboutPage ? t('settings.hidden', 'Hidden') : t('settings.visible', 'Visible')}`}
                                desc={t('settings.aboutSwitchDesc', 'Show the About page on the public site.')} />
                            <SwitchRow on={!hideBetaBanner} disabled={hideBetaSaving} color="#22c55e"
                                onClick={() => toggleSetting('hide_beta_banner', !hideBetaBanner, setHideBetaBanner, setHideBetaSaving, t('settings.betaHidden', 'Beta notice hidden.'), t('settings.betaShown', 'Beta notice is now shown.'))}
                                title={`${t('settings.betaBanner', 'Beta notice')}: ${hideBetaBanner ? t('settings.hidden', 'Hidden') : t('settings.visible', 'Visible')}`}
                                desc={t('settings.betaSwitchDesc', 'The “Beta · report bugs or request access on Discord” notice on the public pages and the login page.')} />
                            <SwitchRow on={allowIndexing} disabled={allowIndexingSaving} color="#22c55e"
                                onClick={() => toggleSetting('allow_search_indexing', !allowIndexing, setAllowIndexing, setAllowIndexingSaving, t('settings.indexingOn', 'Search engines may now index the public pages.'), t('settings.indexingOff', 'Search engines are now asked not to index any page.'))}
                                title={`${t('settings.allowIndexing', 'Allow search engines')}: ${allowIndexing ? t('settings.onCaps', 'On') : t('settings.badgeOff', 'Off')}`}
                                desc={t('settings.allowIndexingDesc', 'Lets Google & co. list the public pages (matches, teams, brackets, seasons). Admin pages and private links are never indexed. Link previews on Discord / X work either way.')} />
                        </Section>
                    </div>

                    <Section tab={tab} id="server-configs" wide icon={<SvIcon name="gear" />} tone="#22c55e" title={t('settings.serverConfigFiles', 'Server Config Files')} desc={t('settings.serverConfigFilesDesc', 'Edit CFG files uploaded to game servers.')}>
                        {!cfgLoaded ? <div className="st-empty">{t('common.loading', 'Loading...')}</div> : (
                            <div className="st-cfgs">
                                {[
                                    { label: 'warmup.cfg', value: cfgWarmup, setter: setCfgWarmup, color: '#f59e0b' },
                                    { label: 'knife.cfg', value: cfgKnife, setter: setCfgKnife, color: '#ef4444' },
                                    { label: 'live.cfg', value: cfgLive, setter: setCfgLive, color: '#22c55e' },
                                ].map(({ label, value, setter, color }) => (
                                    <div key={label} className="st-field">
                                        <span className="st-cfg-label" style={{ '--st-tone': color }}><i />{label}</span>
                                        <textarea className="st-textarea" rows={10} value={value} onChange={e => setter(e.target.value)} placeholder={`// ${label}`} />
                                        <span className="st-cfg-sub" title={t('settings.usedViaRcon', 'Used via RCON if {{file}} is missing on server', { file: label })}>
                                            <SvIcon name="terminal" size={12} />{t('settings.onlineExecution', 'Online Execution')} · {t('settings.autoSynced', 'Auto-synced')}
                                        </span>
                                        <textarea className="st-textarea readonly" rows={3} readOnly value={cfgToOnline(value)} placeholder={t('settings.onlineAutoPlaceholder', 'Generated from the CFG above')} />
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="st-box" style={{ '--st-tone': '#818cf8' }}>
                            <b className="t">{t('settings.howItWorks', 'How it works:')}</b>
                            {t('settings.serverConfigHowItWorks', 'The panel first tries exec warmup.cfg on the game server. If the file doesn\'t exist, it falls back to the Online Execution commands and sends them all in one RCON call (semicolon-separated). The Online Execution field auto-syncs from the CFG above - just edit the CFG and it updates automatically.')}
                        </div>
                        <div className="st-actions">
                            <button type="button" className="st-btn primary" onClick={saveServerConfigs} disabled={cfgSaving || !cfgLoaded}>
                                <Busy busy={cfgSaving} label={t('settings.saving', 'Saving...')}>{t('settings.saveConfigs', 'Save Configs')}</Busy>
                            </button>
                        </div>
                    </Section>
                </div>
            )}

            {/* ── Integrations ── */}
            {tab === 'integrations' && (
                <div className="st-grid">
                    <Section tab={tab} id="demos" wide icon={<SvIcon name="cloud" />} tone="#34a853" title={t('settings.demos', 'Demos')}
                        badge={<Badge on={demoMode === 'auto'} onText={t('settings.auto', 'Auto')} offText={t('settings.badgeOff', 'Off')} color="var(--accent-blue)" />}
                        desc={t('settings.demoUploadConfigDesc', 'Choose where demo files are uploaded after download.')}>
                        <SwitchRow on={demoMode === 'auto'} disabled={demoSaving} color="var(--accent-blue)" onClick={toggleDemoMode}
                            title={t('settings.demoAutoTitle', 'Auto-download demos')}
                            desc={demoMode === 'auto' ? t('settings.demoAutoDesc', 'Auto-download & upload after match. Click to disable.') : t('settings.demoOffDesc', 'Demo auto-download disabled. Click to enable.')} />
                        <div className="st-field">
                            <label>{t('settings.uploadMethod', 'Upload Method')}</label>
                            <div className="st-choices">
                                {UPLOAD_OPTIONS.map(opt => (
                                    <button key={opt.key} type="button" className={`st-choice${uploadMethod === opt.key ? ' on' : ''}`} style={{ '--st-c': opt.color }} onClick={() => setUploadMethod(opt.key)}>
                                        <b>{opt.label}</b><small>{opt.desc}</small>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {uploadMethod === 'pixeldrain' && (<>
                            <div className="st-box" style={{ '--st-tone': '#a855f7' }}>
                                <b className="t">{t('settings.pixeldrainHowTo', 'How to set up Pixeldrain')}</b>
                                <ol>
                                    <li>{t('settings.pixeldrainStep1', 'Register and login on the Pixeldrain website.')}</li>
                                    <li>{t('settings.pixeldrainStep2a', 'Go to')} <a href="https://pixeldrain.com/user/api_keys" target="_blank" rel="noopener noreferrer">pixeldrain.com/user/api_keys</a> {t('settings.pixeldrainStep2b', 'and copy your API key.')}</li>
                                    <li>{t('settings.pixeldrainStep3', 'Paste your API key in the field below.')}</li>
                                </ol>
                                <div className="st-note-warn"><b>{t('settings.note', 'Note:')}</b> {t('settings.pixeldrainNote', 'On the standard free plan, files expire after 60 days, with a download limit of 6 GB per day.')}</div>
                            </div>
                            <div className="st-field">
                                <label>{t('settings.pixeldrainApiKey', 'Pixeldrain API Key')}</label>
                                <input className="st-input mono" type="password" value={pixeldrainApiKey} onChange={e => setPixeldrainApiKey(e.target.value)} placeholder={t('settings.pixeldrainApiKeyPlaceholder', 'Enter your Pixeldrain API key')} />
                                {!pixeldrainKeySet && !pixeldrainApiKey && <div className="st-status" style={{ '--st-tone': '#ef4444' }}><SvIcon name="alert" size={14} />{t('settings.apiKeyRequired', 'API key required - uploads will fail without it.')}</div>}
                            </div>
                        </>)}

                        {uploadMethod === 'storageto' && (<>
                            <div className="st-box" style={{ '--st-tone': '#f97316' }}>
                                <b className="t">{t('settings.storagetoHowTo', 'How to set up storage.to')}</b>
                                <ol>
                                    <li>{t('settings.storagetoStep1', 'Register and login on the storage.to website.')}</li>
                                    <li>{t('settings.storagetoStep2a', 'Go to')} <a href="https://storage.to" target="_blank" rel="noopener noreferrer">storage.to</a> {t('settings.storagetoStep2b', 'account settings and create an API token.')}</li>
                                    <li>{t('settings.storagetoStep3', 'Paste your API token in the field below.')}</li>
                                </ol>
                                <div className="st-note-warn"><b>{t('settings.note', 'Note:')}</b> {t('settings.storagetoNote', 'On the free plan, demos expire after 7 days. Paid plans keep files permanently. Large demos are uploaded in parts automatically.')}</div>
                            </div>
                            <div className="st-field">
                                <label>{t('settings.storagetoApiKey', 'storage.to API Token')}</label>
                                <input className="st-input mono" type="password" value={storagetoApiKey} onChange={e => setStoragetoApiKey(e.target.value)} placeholder={t('settings.storagetoApiKeyPlaceholder', 'Enter your storage.to API token')} />
                                {!storagetoKeySet && !storagetoApiKey && <div className="st-status" style={{ '--st-tone': '#ef4444' }}><SvIcon name="alert" size={14} />{t('settings.apiKeyRequired', 'API key required - uploads will fail without it.')}</div>}
                            </div>
                        </>)}

                        {uploadMethod === 'rclone' && (<>
                            <div className="st-status" style={{ '--st-tone': rcloneRemoteConfigured ? '#34a853' : '#f59e0b' }}>
                                <SvIcon name={rcloneRemoteConfigured ? 'check' : 'alert'} size={14} />
                                {rcloneRemoteConfigured
                                    ? <span>{t('settings.rcloneRemote', 'Rclone remote')} <code>"{rcloneRemoteName}"</code> {t('settings.isConfigured', 'is configured')}</span>
                                    : (rcloneInstalled ? t('settings.rcloneRemoteNotFound', 'Remote "{{name}}" not found. Run rclone config on the server.', { name: rcloneRemoteName }) : t('settings.rcloneNotInstalled', 'Rclone is not installed.'))}
                            </div>
                            <div className="st-fields" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
                                <div className="st-field">
                                    <label>{t('settings.remoteName', 'Remote Name')}</label>
                                    <input className="st-input mono" type="text" value={rcloneRemoteName} onChange={e => setRcloneRemoteName(e.target.value)} placeholder="gdrive" />
                                    <span className="st-hint">{t('settings.remoteNameHintA', 'The name you used in')} <code>rclone config</code></span>
                                </div>
                                <div className="st-field">
                                    <label>{t('settings.remoteFolder', 'Remote Folder')}</label>
                                    <input className="st-input mono" type="text" value={rcloneRemoteFolder} onChange={e => setRcloneRemoteFolder(e.target.value)} placeholder="flux-demos" />
                                    <span className="st-hint">{t('settings.remoteFolderHint', 'Folder on Google Drive where demos will be uploaded.')}</span>
                                </div>
                            </div>
                            <div className="st-box" style={{ '--st-tone': '#4285f4' }}>
                                <b className="t">{t('settings.rcloneHowTo', 'How to set up rclone for Google Drive')}</b>
                                <ol>
                                    <li>{t('settings.rcloneStep1a', 'On your local PC (with a browser), run:')} <code>rclone authorize "drive"</code></li>
                                    <li>{t('settings.rcloneStep2', 'Sign in with your Google account in the browser that opens')}</li>
                                    <li>{t('settings.rcloneStep3', 'Copy the token JSON that rclone outputs')}</li>
                                    <li>{t('settings.rcloneStep4a', 'On your server, create the rclone config file at')} <code>./rclone/rclone.conf</code> {t('settings.rcloneStep4b', '(next to docker-compose.yml):')}</li>
                                </ol>
                                <pre>{`[gdrive]\ntype = drive\nscope = drive\ntoken = {"paste_your_token_here"}`}</pre>
                                <div className="st-note-warn"><b>{t('settings.note', 'Note:')}</b> {t('settings.rcloneNote', 'No Google app verification needed. Rclone uses its own pre-approved OAuth client. Works with personal Gmail.')}</div>
                            </div>
                        </>)}

                        <div className="st-actions">
                            <span className="left">
                                {uploadMethod === 'rclone' && rcloneRemoteConfigured && (<>
                                    <button type="button" className="st-btn" onClick={testRcloneConnection} disabled={rcloneTesting}>
                                        <Busy busy={rcloneTesting} label={t('settings.testing', 'Testing...')}>{t('createSeason.testConnection', 'Test Connection')}</Busy>
                                    </button>
                                    <button type="button" className="st-btn" onClick={testRcloneUpload} disabled={rcloneTestingUpload}>
                                        <Busy busy={rcloneTestingUpload} label={t('settings.uploadingProgress', 'Uploading...')}>{t('settings.testUpload', 'Test Upload')}</Busy>
                                    </button>
                                </>)}
                                {uploadMethod === 'storageto' && storagetoKeySet && (
                                    <button type="button" className="st-btn" onClick={testStoragetoToken} disabled={storagetoTesting}>
                                        <Busy busy={storagetoTesting} label={t('settings.testing', 'Testing...')}>{t('settings.testToken', 'Test Token')}</Busy>
                                    </button>
                                )}
                            </span>
                            <button type="button" className="st-btn primary" onClick={saveUploadConfig} disabled={uploadSaving}>
                                <Busy busy={uploadSaving} label={t('settings.saving', 'Saving...')}>{t('settings.saveConfig', 'Save Config')}</Busy>
                            </button>
                        </div>
                    </Section>

                    <Section tab={tab} id="faceit" icon={<SvIcon name="shield" />} tone="#f59e0b" title={t('settings.faceitIntegration', 'FACEIT Integration')}
                        badge={<Badge on={faceitKeySet} onText={t('settings.active', 'Active')} offText={t('settings.badgeOff', 'Off')} />}
                        desc={t('settings.faceitDesc', 'Show FACEIT level and ELO from roster Steam IDs.')}>
                        <div className="st-field">
                            <label>{t('settings.faceitApiKey', 'FACEIT API Key')}</label>
                            <input className="st-input mono" type="password" value={faceitApiKey} onChange={e => setFaceitApiKey(e.target.value)} placeholder={t('settings.faceitApiKeyPlaceholder', 'Paste FACEIT Data API key')} />
                            <span className="st-hint">{t('settings.faceitApiKeyHint', 'Stored server-side only. Leave the masked value unchanged to keep the current key, or clear the field and save to disable FACEIT lookups.')}</span>
                        </div>
                        <div className="st-actions">
                            <span className="left">
                                <button type="button" className="st-btn" onClick={testFaceitConfig} disabled={faceitTesting}>
                                    <Busy busy={faceitTesting} label={t('settings.testing', 'Testing...')}><SvIcon name="zap" size={13} />{t('settings.testKey', 'Test Key')}</Busy>
                                </button>
                            </span>
                            <button type="button" className="st-btn primary" onClick={saveFaceitConfig} disabled={faceitSaving}>
                                <Busy busy={faceitSaving} label={t('settings.saving', 'Saving...')}>{t('common.save', 'Save')}</Busy>
                            </button>
                        </div>
                    </Section>

                    <Section tab={tab} id="steam" icon={<SvIcon name="key" />} tone="#3b82f6" title={t('settings.steamApiKey', 'Steam API Key')}
                        badge={<Badge on={steamKeySet} onText={t('settings.active', 'Active')} offText={t('settings.badgeOff', 'Off')} />}
                        desc={t('settings.steamApiKeyDesc', 'Reliably resolve steamcommunity.com/id vanity links when adding players.')}>
                        <div className="st-row">
                            <input className="st-input mono" type="text" value={steamApiKey} onChange={e => setSteamApiKey(e.target.value)} placeholder={t('settings.steamApiKeyPlaceholder', 'Steam Web API key')} />
                            <button type="button" className="st-btn primary" onClick={saveSteamApiKey} disabled={steamSaving}>
                                <Busy busy={steamSaving} label="…">{t('common.save', 'Save')}</Busy>
                            </button>
                        </div>
                        <a className="st-hint" href="https://steamcommunity.com/dev/apikey" target="_blank" rel="noopener noreferrer">
                            {t('settings.steamApiKeyGet', 'Get a free key →')}
                        </a>
                    </Section>
                </div>
            )}

            {/* ── System ── */}
            {tab === 'system' && (
                <div className="st-grid">
                    <Section tab={tab} id="online-users" wide icon={<SvIcon name="users" />} tone="#22c55e" title={t('settings.onlineUsers', 'Online Users')}
                        badge={<span className="st-badge" style={{ '--st-b': '#22c55e' }}>{t('settings.onlineCount', '{{count}} online', { count: onlineUsers.length })}</span>}
                        desc={t('settings.onlineUsersModalDesc', 'Admins with the panel open right now. Auto-refreshes every 5s.')}>
                        {onlineUsersLoading && onlineUsers.length === 0 ? <div className="st-empty">{t('settings.loadingOnlineUsers', 'Loading online users...')}</div>
                            : onlineUsers.length === 0 ? <div className="st-empty">{t('settings.noOnlineAdmins', 'No admins are currently connected to the panel.')}</div>
                                : (
                                    <div className="st-users">
                                        {onlineUsers.map(u => {
                                            const roleLabel = u.role === 'super_admin' ? t('settings.superAdmin', 'Super Admin')
                                                : u.role === 'panel_admin' ? t('settings.panelAdmin', 'Panel Admin')
                                                    : (u.role === 'admin' || !u.role) ? t('settings.admin', 'Admin') : u.role;
                                            const roleColor = (u.role === 'super_admin' || u.role === 'admin') ? '#fbbf24' : 'var(--text-secondary)';
                                            return (
                                                <div key={`${u.source}:${u.userId}`} className="st-user">
                                                    <span className="st-avatar">{((u.username || '?').trim().charAt(0) || '?').toUpperCase()}</span>
                                                    <div style={{ minWidth: 0, flex: 1 }}>
                                                        <div className="st-row" style={{ gap: '0.4rem', flexWrap: 'nowrap' }}>
                                                            <span className="name">{u.username}</span>
                                                            <span className="role" style={{ color: roleColor }}>{roleLabel}</span>
                                                            {u.sessions > 1 && <span className="st-badge">{t('settings.tabs', '{{count}} tabs', { count: u.sessions })}</span>}
                                                        </div>
                                                        <div className="meta st-row" style={{ gap: '0.2rem 0.9rem', flexWrap: 'wrap' }}>
                                                            {u.steam_id ? <span className="st-mono">{u.steam_id}</span> : <span>{t('settings.localAccount', 'Local account')}</span>}
                                                            <span>{t('settings.since', 'since {{time}}', { time: u.connectedAt ? formatLocalTime(u.connectedAt) : '-' })}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                        <div className="st-actions">
                            <button type="button" className="st-btn small" onClick={() => loadOnlineUsers(false)} disabled={onlineUsersLoading}>
                                <Busy busy={onlineUsersLoading} label={t('common.loading', 'Loading...')}><SvIcon name="refresh" size={12} />{t('common.refresh', 'Refresh')}</Busy>
                            </button>
                        </div>
                    </Section>

                    <Section tab={tab} id="health" wide icon={<SvIcon name="alert" />} tone="#ef4444" title={t('settings.systemHealth', 'System Health Logs')}
                        desc={t('settings.systemHealthModalDesc', 'Last 30 days. Active and resolved warning/critical events.')}>
                        {healthLoading && healthEvents.length === 0 ? <div className="st-empty">{t('settings.loadingHealthLogs', 'Loading system health logs...')}</div>
                            : healthEvents.length === 0 ? <div className="st-empty">{t('settings.noHealthEvents', 'No warning or critical system health events logged in the last 30 days.')}</div>
                                : (
                                    <div className="st-table-wrap">
                                        <table className="st-table">
                                            <thead><tr>
                                                {[t('common.time', 'Time'), t('common.status', 'Status'), t('settings.severity', 'Severity'), t('settings.event', 'Event'), t('settings.message', 'Message'), t('settings.value', 'Value')].map(h => <th key={h}>{h}</th>)}
                                            </tr></thead>
                                            <tbody>
                                                {healthEvents.map(ev => {
                                                    const sev = ev.severity === 'critical' ? '#ef4444' : '#f59e0b';
                                                    const ts = ev.created_at ? formatLocalDateTime(ev.created_at) : '-';
                                                    const value = [ev.value, ev.unit].filter(Boolean).join(' ') || '-';
                                                    return (
                                                        <tr key={ev.id}>
                                                            <td className="mono" title={ts}>{ts}</td>
                                                            <td><span className="st-badge" style={{ '--st-b': ev.status === 'resolved' ? '#22c55e' : sev }}>{ev.status}</span></td>
                                                            <td><span className="st-badge" style={{ '--st-b': sev }}>{ev.severity}</span></td>
                                                            <td className="strong" title={ev.title || ev.event_key}>{ev.title || ev.event_key}</td>
                                                            <td title={ev.message || ''}>{ev.message || '-'}</td>
                                                            <td className="mono" title={value}>{value}</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                        <div className="st-actions">
                            <button type="button" className="st-btn small" onClick={loadHealthEvents} disabled={healthLoading}>
                                <Busy busy={healthLoading} label={t('common.loading', 'Loading...')}><SvIcon name="refresh" size={12} />{t('common.refresh', 'Refresh')}</Busy>
                            </button>
                            <button type="button" className="st-btn small primary" onClick={downloadHealthEvents} disabled={healthDownloading}>
                                <Busy busy={healthDownloading} label={t('settings.preparing', 'Preparing...')}><SvIcon name="download" size={12} />{t('settings.downloadCsv', 'Download CSV')}</Busy>
                            </button>
                        </div>
                    </Section>
                </div>
            )}

            {/* ── API traffic ── */}
            {tab === 'traffic' && (
                <div className="st-grid">
                    <Section tab={tab} id="api-traffic" wide icon={<SvIcon name="zap" />} tone="#3b82f6" title={t('settings.apiTraffic', 'API traffic')}
                        desc={t('settings.apiTrafficDesc', 'Every request to the panel API, counted per minute and kept for 30 days: who sent it, errors, response time, the busiest endpoints and API keys.')}>
                        <ApiTrafficPanel />
                    </Section>
                </div>
            )}

            {/* ── Updates ── */}
            {tab === 'updates' && (
                <div className="st-grid">
                    <Section tab={tab} id="panel-update" wide icon={<SvIcon name="download" />} tone="#22c55e" title={t('settings.panelUpdates', 'Panel updates')}
                        desc={t('settings.panelUpdatesDesc', 'Install a new Flux release with one click. Never automatic, and not possible while a match is live.')}>
                        <UpdatesPanel />
                    </Section>
                </div>
            )}

            <AlertModal show={alert.show} message={alert.message} type={alert.type} onClose={() => setAlert(a => ({ ...a, show: false }))} />
        </div>
    );
}

export default Settings;
